import type { Theme as MUITheme } from '@material-ui/core';
import type { ContainerProps } from '@material-ui/core/Container';
import type { PaletteColor } from '@material-ui/core/styles/createPalette';

import type { IKlpStyles, KlpColor } from './IKlpStyles';
import type { IKlpFontVariant } from './KlpFontVariant';
import type { SpacingDefinition } from './KlpSpacing';
import type { IKlpSpacing } from './KlpSpacing';

export type KlpStyled = IKlpStyles; // & WithStyles;

declare module '@material-ui/core/styles/createTheme' {
    export interface ThemeOptions {
        klpVariant: IKlpFontVariant;
        klpSpacing: IKlpSpacing;
        mainContent: {
            paddingBottom: number;
        };
        header: {
            height: number;
        };
        sidebar: {
            open: {
                width: number;
                contentWidth: number;
            };
            closed: {
                width: number;
            };
        };
        byggSelector: {
            open: {
                width: number;
            };
            closed: {
                width: number;
            };
            transition: string;
        };

        kommentarerMenuOpen: {
            width: number;
        };

        layout: {
            maxWidth: ContainerProps['maxWidth'];
            transition: string;
        };
    }

    export interface Theme {
        klpVariant: IKlpFontVariant;
        klpSpacing: IKlpSpacing;
        mainContent: {
            paddingBottom: number;
        };
        header: {
            height: number;
        };
        sidebar: {
            open: {
                width: number;
                contentWidth: number;
            };
            closed: {
                width: number;
            };
        };
        byggSelector: {
            open: {
                width: number;
            };
            closed: {
                width: number;
            };
            transition: string;
        };
        kommentarerMenuOpen: {
            width: number;
        };

        layout: {
            maxWidth: ContainerProps['maxWidth'];
            transition: string;
        };
    }

    export interface Palette {
        tertiary: PaletteColor;
    }
}

declare module '@material-ui/core/styles/createPalette' {
    export interface PaletteColor {
        mediumDark?: string;
        medium?: string;
        mediumLight?: string;
        veryLight?: string;
    }

    export interface SimplePaletteColorOptions {
        mediumDark?: string;
        medium?: string;
        mediumLight?: string;
        veryLight?: string;
    }

    export interface PaletteOptions {
        tertiary?: PaletteColorOptions;
    }

    export interface Palette {
        tertiary?: PaletteColor;
    }
}

export const removeKlpStyles = <P extends IKlpStyles>(props: P) => {
    const {
        klpHeadingLevel: _klpHeadingLevel,
        klpTypographyVariant: _klpTypographyVariant,
        klpPalette: _klpPalette,
        klpColor: _klpColor,
        margin: _margin,
        marginHorizontal: _marginHorizontal,
        marginVertical: _marginVertical,
        marginLeft: _marginLeft,
        marginRight: _marginRight,
        marginTop: _marginTop,
        marginBottom: _marginBottom,
        padding: _padding,
        paddingHorizontal: _paddingHorizontal,
        paddingVertical: _paddingVertical,
        paddingLeft: _paddingLeft,
        paddingRight: _paddingRight,
        paddingTop: _paddingTop,
        paddingBottom: _paddingBottom,
        justifyContent: _justifyContent,
        ...rest
    } = props;

    return rest as Omit<P, keyof KlpStyled>;
};

export const KlpStyles = (extraStyles?: any) => (theme: MUITheme) => {
    const extend = extraStyles ? extraStyles(theme) : {};
    const getThemeValue = (
        mode: keyof SpacingDefinition,
        cssProp: string,
        props: IKlpStyles,
    ) => {
        const {
            margin,
            marginHorizontal,
            marginVertical,
            marginLeft,
            marginRight,
            marginTop,
            marginBottom,
            padding,
            paddingHorizontal,
            paddingVertical,
            paddingLeft,
            paddingRight,
            paddingTop,
            paddingBottom,
        } = props;

        switch (cssProp) {
            case 'margin': {
                const prop =
                    marginLeft ||
                    marginRight ||
                    marginTop ||
                    marginBottom ||
                    marginHorizontal ||
                    marginVertical ||
                    margin;
                return prop && theme.klpSpacing[prop]
                    ? theme.klpSpacing[prop][mode]
                    : undefined;
            }
            case 'padding': {
                const prop =
                    paddingLeft ||
                    paddingRight ||
                    paddingTop ||
                    paddingBottom ||
                    paddingHorizontal ||
                    paddingVertical ||
                    padding;
                return prop && theme.klpSpacing[prop]
                    ? theme.klpSpacing[prop][mode]
                    : undefined;
            }
        }
        return;
    };
    return {
        ...extend,
        klpStyles: {
            color: (props: KlpStyled) => {
                const { klpColor, klpPalette } = props;

                if (
                    typeof klpColor === 'string' &&
                    klpColor.charAt(0) === '#'
                ) {
                    return klpColor;
                }

                if (klpPalette && klpColor && theme && theme.palette) {
                    const paletteKey = klpPalette || 'primary';
                    const palette = theme.palette[paletteKey];
                    if (palette) {
                        return palette[(props.klpColor || 'main') as KlpColor];
                    }
                }
                return;
            },

            fontSize: (props: KlpStyled) =>
                props.klpHeadingLevel
                    ? undefined
                    : theme.klpVariant[props.klpTypographyVariant || 'default']
                          .fontSize,
            lineHeight: (props: KlpStyled) =>
                props.klpHeadingLevel
                    ? undefined
                    : theme.klpVariant[props.klpTypographyVariant || 'default']
                          .lineHeight,
            justifyContent: (props: KlpStyled) =>
                props.justifyContent || 'inherit',
            [theme.breakpoints.down('sm')]: {
                marginTop: ({ marginTop, marginVertical, margin }: KlpStyled) =>
                    getThemeValue('mobile', 'margin', {
                        marginTop,
                        marginVertical,
                        margin,
                    }),

                marginBottom: ({
                    marginBottom,
                    marginVertical,
                    margin,
                }: KlpStyled) =>
                    getThemeValue('mobile', 'margin', {
                        marginBottom,
                        marginVertical,
                        margin,
                    }),

                marginLeft: ({
                    marginLeft,
                    marginHorizontal,
                    margin,
                }: KlpStyled) =>
                    getThemeValue('mobile', 'margin', {
                        marginLeft,
                        marginHorizontal,
                        margin,
                    }),

                marginRight: ({
                    marginRight,
                    marginHorizontal,
                    margin,
                }: KlpStyled) =>
                    getThemeValue('mobile', 'margin', {
                        marginRight,
                        marginHorizontal,
                        margin,
                    }),

                paddingTop: ({
                    paddingTop,
                    paddingVertical,
                    padding,
                }: KlpStyled) =>
                    getThemeValue('mobile', 'padding', {
                        paddingTop,
                        paddingVertical,
                        padding,
                    }),

                paddingBottom: ({
                    paddingBottom,
                    paddingVertical,
                    padding,
                }: KlpStyled) =>
                    getThemeValue('mobile', 'padding', {
                        paddingBottom,
                        paddingVertical,
                        padding,
                    }),

                paddingLeft: ({
                    paddingLeft,
                    paddingHorizontal,
                    padding,
                }: KlpStyled) =>
                    getThemeValue('mobile', 'padding', {
                        paddingLeft,
                        paddingHorizontal,
                        padding,
                    }),

                paddingRight: ({
                    paddingRight,
                    paddingHorizontal,
                    padding,
                }: KlpStyled) =>
                    getThemeValue('mobile', 'padding', {
                        paddingRight,
                        paddingHorizontal,
                        padding,
                    }),
            },
            [theme.breakpoints.only('md')]: {
                marginTop: ({ marginTop, marginVertical, margin }: KlpStyled) =>
                    getThemeValue('tablet', 'margin', {
                        marginTop,
                        marginVertical,
                        margin,
                    }),

                marginBottom: ({
                    marginBottom,
                    marginVertical,
                    margin,
                }: KlpStyled) =>
                    getThemeValue('tablet', 'margin', {
                        marginBottom,
                        marginVertical,
                        margin,
                    }),

                marginLeft: ({
                    marginLeft,
                    marginHorizontal,
                    margin,
                }: KlpStyled) =>
                    getThemeValue('tablet', 'margin', {
                        marginLeft,
                        marginHorizontal,
                        margin,
                    }),

                marginRight: ({
                    marginRight,
                    marginHorizontal,
                    margin,
                }: KlpStyled) =>
                    getThemeValue('tablet', 'margin', {
                        marginRight,
                        marginHorizontal,
                        margin,
                    }),

                paddingTop: ({
                    paddingTop,
                    paddingVertical,
                    padding,
                }: KlpStyled) =>
                    getThemeValue('tablet', 'padding', {
                        paddingTop,
                        paddingVertical,
                        padding,
                    }),

                paddingBottom: ({
                    paddingBottom,
                    paddingVertical,
                    padding,
                }: KlpStyled) =>
                    getThemeValue('tablet', 'padding', {
                        paddingBottom,
                        paddingVertical,
                        padding,
                    }),

                paddingLeft: ({
                    paddingLeft,
                    paddingHorizontal,
                    padding,
                }: KlpStyled) =>
                    getThemeValue('tablet', 'padding', {
                        paddingLeft,
                        paddingHorizontal,
                        padding,
                    }),

                paddingRight: ({
                    paddingRight,
                    paddingHorizontal,
                    padding,
                }: KlpStyled) =>
                    getThemeValue('tablet', 'padding', {
                        paddingRight,
                        paddingHorizontal,
                        padding,
                    }),
            },
            [theme.breakpoints.up('md')]: {
                marginTop: ({ marginTop, marginVertical, margin }: KlpStyled) =>
                    getThemeValue('desktop', 'margin', {
                        marginTop,
                        marginVertical,
                        margin,
                    }),

                marginBottom: ({
                    marginBottom,
                    marginVertical,
                    margin,
                }: KlpStyled) =>
                    getThemeValue('desktop', 'margin', {
                        marginBottom,
                        marginVertical,
                        margin,
                    }),

                marginLeft: ({
                    marginLeft,
                    marginHorizontal,
                    margin,
                }: KlpStyled) =>
                    getThemeValue('desktop', 'margin', {
                        marginLeft,
                        marginHorizontal,
                        margin,
                    }),

                marginRight: ({
                    marginRight,
                    marginHorizontal,
                    margin,
                }: KlpStyled) =>
                    getThemeValue('desktop', 'margin', {
                        marginRight,
                        marginHorizontal,
                        margin,
                    }),

                paddingTop: ({
                    paddingTop,
                    paddingVertical,
                    padding,
                }: KlpStyled) =>
                    getThemeValue('desktop', 'padding', {
                        paddingTop,
                        paddingVertical,
                        padding,
                    }),

                paddingBottom: ({
                    paddingBottom,
                    paddingVertical,
                    padding,
                }: KlpStyled) =>
                    getThemeValue('desktop', 'padding', {
                        paddingBottom,
                        paddingVertical,
                        padding,
                    }),

                paddingLeft: ({
                    paddingLeft,
                    paddingHorizontal,
                    padding,
                }: KlpStyled) =>
                    getThemeValue('desktop', 'padding', {
                        paddingLeft,
                        paddingHorizontal,
                        padding,
                    }),

                paddingRight: ({
                    paddingRight,
                    paddingHorizontal,
                    padding,
                }: KlpStyled) =>
                    getThemeValue('desktop', 'padding', {
                        paddingRight,
                        paddingHorizontal,
                        padding,
                    }),
            },
        },
    };
};
